import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useSnackbar } from "notistack";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";

import { InfoBox } from "@/components/InfoBox";
import { BarLoading } from "@/components/Loading/BarLoading";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { McpConnectionTable } from "@/pages/applications/pages/mcpConnections/components/McpConnectionTable";
import { RevokeMcpConnectionModal } from "@/pages/applications/pages/mcpConnections/components/RevokeMcpConnectionModal";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

type McpApiKey = InterludeTypes["McpApiKey"];

const styles = {
  pageTitle: {
    marginBottom: 0,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    width: "100%",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const McpConnectionsPageInternal: React.FC =
  function McpConnectionsPageInternal() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const [revokingConnection, setRevokingConnection] =
      useState<McpApiKey | null>(null);

    const organizationSettingQuery =
      interlude.organization.useSuspenseOrganizationSetting();
    const mcpEnabled = organizationSettingQuery.data.mcpEnabled;

    /**
     * Fetched whether or not the organization has MCP enabled. Turning it off
     * stops new connections but leaves the existing ones working, so unlike
     * the API key page this one still lists and revokes them -- interlude
     * leaves both endpoints ungated for exactly that.
     */
    const connectionsQuery = interlude.mcp.useListMcpApiKeys();

    const handleRetry = useHandler(() => {
      connectionsQuery.refetch();
    });

    const handleContactUsClick = useHandler(() => {
      window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
    });

    const handleRevoke = useHandler((connection: McpApiKey) => {
      setRevokingConnection(connection);
    });

    const handleCloseRevokeModal = useHandler(() => {
      setRevokingConnection(null);
    });

    const handleRevokeSuccess = useHandler(() => {
      enqueueSnackbar(
        t("mcpConnection.toast.revokeSuccess", "MCP connection revoked."),
        { variant: "success" },
      );
    });

    return (
      <Stack gap="8px">
        <Box sx={styles.pageTitle}>
          <PageTitle>
            {t("mcpConnection.page.title", "Authorized MCP connections")}
          </PageTitle>
        </Box>

        <Box sx={styles.section}>
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t(
              "mcpConnection.page.description",
              "AI clients that people in your organization have authorized through Crescendo Lab MCP. Each connection is created when someone signs in and grants access from their AI client, not a key managed here. Revoke one to withdraw that authorization.",
            )}
          </Typography>

          {!mcpEnabled ? (
            <InfoBox
              variant="warning"
              title={t(
                "mcpConnection.page.notEntitled.title",
                "Crescendo Lab MCP is not enabled for this organization",
              )}
              description={t(
                "mcpConnection.page.notEntitled.description",
                "No new AI clients can be authorized. Connections authorized before it was turned off keep working until you revoke them here. Contact us to enable Crescendo Lab MCP.",
              )}
              action={{
                label: t("common.contactUs"),
                onClick: handleContactUsClick,
              }}
            />
          ) : null}

          <McpConnectionTable
            connections={connectionsQuery.data?.data ?? []}
            isLoading={connectionsQuery.isPending}
            isError={connectionsQuery.isError}
            onRetry={handleRetry}
            onRevoke={handleRevoke}
          />
        </Box>

        {/*
         * No refetch after a revoke: the zodios wrapper invalidates the whole
         * `mcp` namespace when a mutation settles, so the list reloads itself.
         */}
        <RevokeMcpConnectionModal
          open={!!revokingConnection}
          connection={revokingConnection}
          onClose={handleCloseRevokeModal}
          onSuccess={handleRevokeSuccess}
        />
      </Stack>
    );
  };

export const McpConnectionsPage: React.FC = function McpConnectionsPage() {
  return (
    <PageLayout>
      <Suspense fallback={<BarLoading />}>
        <McpConnectionsPageInternal />
      </Suspense>
    </PageLayout>
  );
};
